import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import TanStackGrid from './TanStackGrid';
import { userAppContext } from '../../Context/Context.jsx';
import buildResultEndpoint from '../../utils/buildResultEndpoint';
import fetchEndpoint from '../../services/fetchEndpoint.jsx';
import Filters from './Filters.jsx';
import dropdownLists from './DropdownLists.jsx';
import ErrorMessage from '../UI/ErrorMessage.jsx';
import { SortingState, OnChangeFn, RowSelectionState } from '@tanstack/react-table';

import {
  getSourceList,
  isSourceEnabledForTab,
} from '../../modules/search/searchGrid/helpers/sourceListHelpers.ts';
import { isEmpty, isArray } from 'lodash';
import {
  decodeData,
  encodeData,
  encodeFiltersToParams,
  decodeFiltersFromParams,
} from '../../utils/encodeUtils.ts';
import ActiveFiltersBar from '../UI/ActiveFiltersBar.tsx';
import SharedDialog from '../../global/Modal/Modal.tsx';
import { getFilterLabel } from '../../modules/search/searchGrid/helpers/getFiltersLabels.ts';
import { getFiltersForUser, setFiltersForUser } from '../../utils/filtersStorage.ts';
import Alert from '../../global/Alert/Alert.tsx';

const SHOW_SUMMARIZE = process.env.REACT_APP_SHOW_SUMMARIZE === 'true';

// Types and Interfaces
interface SearchItem {
  id: string;
  name: string;
}

interface FilterValue {
  name: string;
  value: string;
  operator?: string;
  type?: string;
  integer?: boolean;
  isEvidence?: boolean;
}

interface ColumnDef {
  name: string;
  header: React.ReactNode | string;
  sortable?: boolean;
  render?: (info: { data: any; value: any }) => React.ReactNode;
  minWidth?: number;
  type?: string;
}

interface GridProps {
  columns: any[];
  columnVisibilityModel?: Record<string, boolean>;
  columnOrder?: string[];
  setColumnOrder?: (order: string[]) => void;
  view: string;
  source: string;
  tab: string;
  searchItems: any[];
  isEvidence: boolean;
  isMapping?: boolean;
  setTotalResults: (count: number) => void;
  getFilterValueRef?: any;
  onData: (areItemsSelected: boolean, selectedData: any[]) => void;
  sourceWeights?: any;
  defaultFilterValue?: any[];
}

interface CacheEntry {
  data: any[];
  totalCount: number;
  timestamp: number;
}

function Grid({
  columns,
  columnVisibilityModel,
  columnOrder,
  setColumnOrder,
  view,
  source,
  tab,
  searchItems,
  isEvidence,
  isMapping,
  setTotalResults,
  getFilterValueRef,
  onData,
  sourceWeights,
  ...rest
}: GridProps) {
  const navigate = useNavigate();
  const {
    isAuthenticated,
    authorization,
    setIsAuthenticated,
    groups,
    setGroups,
    setAuthError,
    setAuthorization,
    restrictGridDisplay,
    restrictGridResultCount,
    restrictBrowseAll,
    restrictSources,
    restrictChemical,
    mappingContent,
    user,
  } = userAppContext();

  const gridContainerRef = useRef<HTMLDivElement | null>(null);

  const [isSessionExpired, setIsSessionExpired] = useState<boolean>(false);
  const [rowData, setRowData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [skip, setSkip] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const [orderByParam, setOrderByParam] = useState<string | undefined>(undefined);
  const [order, setOrder] = useState<number | undefined>(undefined);
  const [showFiltersModal, setShowFiltersModal] = useState<boolean>(false);
  const [URLWithFilters, setURLWithFilters] = useState<string>('');
  const [showCopiedAlert, setShowCopiedAlert] = useState<boolean>(false);
  const [pendingFiltersToSave, setPendingFiltersToSave] = useState<FilterValue[] | null>(null);
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [filtersLoadedFromURL, setFiltersLoadedFromURL] = useState<boolean>(false);

  const cacheRef = useRef<Record<string, CacheEntry>>({});

  const [defaultFilterValue, setDefaultFilterValue] = useState<FilterValue[]>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const encodedFilters = urlParams.get('f');

    let urlFilters = [];
    if (encodedFilters) {
      try {
        urlFilters = decodeData(encodedFilters);
      } catch (error) {
        console.error('❌ Error decoding filters from URL:', error);
      }
    }

    const baseFilters = Filters.filter(f => f);
    const propFilters = Array.isArray(rest.defaultFilterValue) ? rest.defaultFilterValue : [];

    if (urlFilters.length > 0) {
      const finalFilters = [...baseFilters, ...urlFilters];
      return finalFilters;
    }

    if (propFilters.length > 0) {
      return [...baseFilters, ...propFilters];
    }

    return baseFilters;
  });

  const [incompleteFilterMessage, setIncompleteFilterMessage] = useState<string | null>(null);
  const [selectedRow, setSelected] = useState<any[]>([]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const encodedFilters = urlParams.get('f');

    if (encodedFilters && !filtersLoadedFromURL) {
      try {
        const urlFilters = decodeData(encodedFilters);
        if (urlFilters.length > 0) {
          const baseFilters = Filters.filter(f => f);
          setDefaultFilterValue([...baseFilters, ...urlFilters]);
          setFiltersLoadedFromURL(true);
        }
      } catch (error) {
        console.error('Error decoding filters from URL:', error);
      }
    }
  }, [filtersLoadedFromURL]);

  const onSelectionChange = useCallback(
    event => {
      const selectedNodes = event.api.getSelectedNodes();
      const selectedData = selectedNodes.map(node => node.data);
      const areItemsSelected = selectedData.length > 0;

      setSelected(selectedData);
      onData(areItemsSelected, selectedData);
    },
    [onData],
  );

  const isTrial = groups?.includes('ROLE_TRIAL');
  const isAcademic = groups?.includes('ROLE_ACADEMIC');

  const validSourceList = getSourceList({ tab, restrictSources });
  const allValidSources = Array.isArray(validSourceList)
    ? validSourceList
    : Object.values(validSourceList).flat();

  const isInvalidSource = source && !isSourceEnabledForTab(source, tab, restrictSources);

  if (getFilterValueRef) {
    getFilterValueRef.current = () => defaultFilterValue;
  }

  const sortModel = useMemo(() => {
    if (!orderByParam) return [];
    return [
      {
        colId: orderByParam,
        sort: order === 1 ? 'asc' : 'desc',
      },
    ];
  }, [orderByParam, order]);

  function getPageSizes() {
    if (!isAuthenticated) return [10];
    if (isTrial) return [10, 30];
    if (restrictBrowseAll) return [10, 30];
    return [10, 20, 25, 40, 50, 100];
  }

  function checkIncompleteFilters(filterValue) {
    const incompleteFilters = [];

    filterValue.forEach(filter => {
      if (filter.name === 'reference' && filter.value && filter.value.length < 8) {
        incompleteFilters.push('reference');
      }
      if (filter.name === 'pmid' && filter.value && filter.value.length < 8) {
        incompleteFilters.push('PMID');
      }
    });

    if (incompleteFilters.length > 0) {
      const filterNames = incompleteFilters.join(' y ');
      return `You must write the full ${filterNames} to filter`;
    }

    return null;
  }

  const filterValuesKey = useMemo(
    () =>
      JSON.stringify(
        defaultFilterValue
          .filter(f => f.value)
          .map(f => ({ name: f.name, value: f.value, operator: f.operator })),
      ),
    [defaultFilterValue],
  );

  const prevDataRef = useRef<{ data: any[]; count: number }>({ data: [], count: 0 });
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }

    fetchTimeoutRef.current = setTimeout(() => {
    if (!(view && source && tab && searchItems)) {
      return;
    }

    const incompleteMessage = checkIncompleteFilters(defaultFilterValue);
    setIncompleteFilterMessage(incompleteMessage);

    if (incompleteMessage) {
      setRowData([]);
      setTotalCount(0);
      setTotalResults(0);
      prevDataRef.current = { data: [], count: 0 };
      return;
    }

    let urlEndpoint = buildResultEndpoint({
      view,
      source,
      tab,
      searchItems,
      isEvidence,
      isMapping,
      mappingContent,
      pageNum: restrictGridResultCount || (restrictBrowseAll && view === 'ALL') ? 0 : skip / limit,
      pageSize: limit,
      orderByParam,
      order,
      defaultFilterValue: defaultFilterValue as any,
      sourceWeights,
    });

    if (!isAuthenticated && skip !== 0) {
      setShowPopup(true);
      return;
    }

    if (restrictBrowseAll && view === 'ALL' && skip !== 0) {
      setShowPopup(true);
      return;
    }

    if (isTrial && skip !== 0) {
      setShowPopup(true);
      return;
    }

    if (!urlEndpoint) return;
    urlEndpoint = !isAuthenticated ? `${urlEndpoint}&skey=unauth` : urlEndpoint;

    // Check cache first - but only use if it's recent (5 minutes)
    const cacheKey = urlEndpoint;
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    if (cacheRef.current[cacheKey]) {
      const cached = cacheRef.current[cacheKey];
      const isRecentCache = Date.now() - cached.timestamp < CACHE_DURATION;
      
      if (isRecentCache) {
        setRowData(cached.data);
        setTotalCount(cached.totalCount);
        setTotalResults(cached.totalCount);
        setIsSessionExpired(false);
        prevDataRef.current = { data: cached.data, count: cached.totalCount };
        return;
      } else {
        // Remove stale cache
        delete cacheRef.current[cacheKey];
      }
    }

    setLoading(true);
    (fetchEndpoint as any)(urlEndpoint, authorization, setAuthorization, setIsAuthenticated, setGroups, setAuthError)
      .then(res => {
        const data = res?.payload;
        const totalElements = res?.paging ? res.paging.totalElements : 0;

        if (data) {
          let totalCountValue = parseInt(totalElements);
          
          const hasSpecificSearchItems = searchItems && searchItems.length > 0 && 
            !searchItems.some(item => item.id === 'ALL');
          
          const urlParams = new URLSearchParams(window.location.search);
          const identsParam = urlParams.get('idents');
          const isFromAdvancedSearch = identsParam && identsParam !== 'ALL' && identsParam.includes('-');
          
          if (hasSpecificSearchItems || isFromAdvancedSearch) {
            const filteredCount = res?.paging?.filteredElements || res?.paging?.filteredCount || res?.filteredCount;
            
            if (filteredCount && filteredCount !== totalCountValue && filteredCount < totalCountValue) {
              totalCountValue = parseInt(filteredCount);

            } else {
              
              if (data.length === 0) {
                totalCountValue = 0;
              } else if (skip === 0 && data.length < limit) {
                totalCountValue = data.length;
              } else {
                const isUnreasonablyHigh = totalCountValue > 100000;
                
                if (isUnreasonablyHigh) {
                  if (data.length === limit) {
                    totalCountValue = Math.min(data.length * 50, 10000); 
                  } else {
                    totalCountValue = skip + data.length;
                  }
                }
              }
            }
          }

          setRowData(data);
          setTotalCount(totalCountValue);
          setTotalResults(totalCountValue);
          setIsSessionExpired(false);

          prevDataRef.current = { data, count: totalCountValue };

          // Cache the response
          cacheRef.current[cacheKey] = {
            data: data,
            totalCount: totalCountValue,
            timestamp: Date.now(),
          };

          const cacheKeys = Object.keys(cacheRef.current);
          if (cacheKeys.length > 20) {
            const sortedKeys = cacheKeys.sort(
              (a, b) => cacheRef.current[a].timestamp - cacheRef.current[b].timestamp,
            );
            sortedKeys.slice(0, cacheKeys.length - 20).forEach(key => {
              delete cacheRef.current[key];
            });
          }
        } else if (res?.status === 403) {
          setIsSessionExpired(true);
          setRowData([]);
          setTotalCount(0);
          prevDataRef.current = { data: [], count: 0 };
        } else {
          setRowData([]);
          setTotalCount(0);
          prevDataRef.current = { data: [], count: 0 };
        }
      })
      .catch(error => {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error:', error);
        }
        setRowData([]);
        setTotalCount(0);
      })
      .finally(() => {
        setLoading(false);
      });
    }, 300);

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [
    view,
    source,
    tab,
    searchItems,
    skip,
    limit,
    order,
    orderByParam,
    filterValuesKey,
    sourceWeights,
    isEvidence,
    isMapping,
    isAuthenticated,
    mappingContent,
  ]);

  // Sorting and Filtering
  const onSortChanged = useCallback(event => {
    const sortModel = event.api.getSortModel();
    if (sortModel && sortModel.length > 0) {
      const sort = sortModel[0];
      setOrder(sort.sort === 'asc' ? 1 : -1);
      setOrderByParam(sort.colId);
    } else {
      setOrder(undefined);
      setOrderByParam(undefined);
    }
  }, []);

  useEffect(() => {
    if (filtersLoadedFromURL) return;

    const p = new URLSearchParams(location.search);

    let decodedFilters = decodeFiltersFromParams(p);

    if (decodedFilters.length === 0) {
      const filterToken = p.get('f');
      if (filterToken) {
        decodedFilters = decodeData(filterToken);
      }
    }

    if (decodedFilters?.length) {
      const relevantFilters = decodedFilters.filter(f => f.isEvidence === isEvidence);

      if (relevantFilters.length > 0) {
        setDefaultFilterValue(() => {
          const baseFiltersMap = new Map(Filters.filter(f => f).map(f => [f.name, { ...f, value: '' }]));

          relevantFilters.forEach(f => {
            const baseFilter = Filters.find(bf => bf.name === f.name);
            if (baseFilter) {
              baseFiltersMap.set(f.name, {
                ...baseFilter,
                ...f,
                type: baseFilter.type,
                integer: baseFilter.integer,
              });
            } else {
              baseFiltersMap.set(f.name, f);
            }
          });

          const result = Array.from(baseFiltersMap.values());
          return result;
        });

        setFiltersLoadedFromURL(true);
      }
    } else {
      setFiltersLoadedFromURL(true);
    }
  }, [location.search, isEvidence, filtersLoadedFromURL]);

  const onFilterValueChange = useCallback(
    value => {
      const scrollX = gridContainerRef.current?.scrollLeft || 0;
      setDefaultFilterValue(value);

      const filters = value.filter(v => v.value);

      const filtersWithEvidence = filters.map(filter => ({ ...filter, isEvidence }));

      setPendingFiltersToSave(filtersWithEvidence);

      const currentUrl = new URL(window.location.href);

      currentUrl.searchParams.delete('f');

      const tablePrefix = isEvidence ? 'evidence' : 'summary';
      const keysToDelete = [];
      for (const key of currentUrl.searchParams.keys()) {
        if (key.startsWith(`filters-${tablePrefix}-`)) {
          keysToDelete.push(key);
        }
        if (key.startsWith(`filters[${tablePrefix}][`)) {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach(key => currentUrl.searchParams.delete(key));

      if (filtersWithEvidence.length > 0) {
        const filterParams = encodeFiltersToParams(filtersWithEvidence);
        for (const [key, value] of filterParams.entries()) {
          currentUrl.searchParams.set(key, value);
        }
      }

      window.history.replaceState({}, '', currentUrl.href);
      setURLWithFilters(currentUrl.href);

      setTimeout(() => {
        if (gridContainerRef.current) gridContainerRef.current.scrollLeft = scrollX;
      }, 0);
    },
    [isEvidence],
  );

  const onColumnMoved = useCallback(
    event => {
      if (setColumnOrder && event.finished) {
        const columnState = event.api.getColumnState();
        const newOrder = columnState.map(col => col.colId);
        setColumnOrder(newOrder);
      }
    },
    [setColumnOrder],
  );

  const onPaginationChanged = useCallback(event => {
    if (event.newPage) {
      const currentPage = event.api.paginationGetCurrentPage();
      const pageSize = event.api.paginationGetPageSize();
      setSkip(currentPage * pageSize);
      setLimit(pageSize);
    }
  }, []);

  const handleSaveFilters = useCallback(() => {
    if (!pendingFiltersToSave) return;

    const existingFilters = getFiltersForUser(user);

    const searchContext = {
      id: `search-${Date.now()}`,
      tab,
      view,
      source,
      idents: searchItems ? searchItems.map(item => item.id).join('-') : '',
      identsNames: searchItems ? searchItems.map(item => item.name).join(', ') : '',
      isEvidence,
      filters: pendingFiltersToSave,
      savedAt: new Date().toISOString(),
    };

    const updatedFilters = [...existingFilters, searchContext];

    setFiltersForUser(user, updatedFilters);

    setAlertMessage('Filters saved successfully');
    setShowCopiedAlert(true);
    setTimeout(() => setShowCopiedAlert(false), 3000);
    setShowFiltersModal(false);
  }, [pendingFiltersToSave, isEvidence, tab, view, source, searchItems, user]);

  const handleClearAllFilters = useCallback(() => {
    const baseFilters = Filters.filter(f => f).map(f => ({ ...f, value: '' }));

    setSkip(0);

    try {
      const url = new URL(window.location.href);

      // Remove old format
      url.searchParams.delete('f');

      const tablePrefix = isEvidence ? 'evidence' : 'summary';
      const keysToDelete = [];
      for (const key of url.searchParams.keys()) {
        if (key.startsWith(`filters-${tablePrefix}-`)) {
          keysToDelete.push(key);
        }
        if (key.startsWith(`filters[${tablePrefix}][`)) {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach(key => url.searchParams.delete(key));

      window.history.replaceState({}, '', url);
      setURLWithFilters('');
    } catch (e) {
      // ignore URL errors
    }

    setDefaultFilterValue(baseFilters);
    onFilterValueChange(baseFilters);
  }, [onFilterValueChange, isEvidence]);

  const handleClosePopup = useCallback(() => {
    setShowPopup(false);
    setSkip(0);
  }, []);

  const handleRemoveFilter = useCallback(
    filterToRemove => {
      const newFilters = defaultFilterValue.map(f => {
        if (
          f.name === filterToRemove.name &&
          (f.type !== 'number' || f.operator === filterToRemove.operator)
        ) {
          return { ...f, value: '' };
        }
        return f;
      });

      setDefaultFilterValue(newFilters);
      onFilterValueChange(newFilters);
    },
    [defaultFilterValue, onFilterValueChange],
  );

  const prevTabRef = useRef<string>(tab);

  useEffect(() => {
    if (prevTabRef.current !== tab) {
      const baseFilters = Filters.filter(f => f).map(f => ({ ...f, value: '' }));
      setDefaultFilterValue(baseFilters);
      onFilterValueChange(baseFilters);
      setFiltersLoadedFromURL(false);
      prevTabRef.current = tab;
    }
  }, [tab]);

  // Memoized values for TanStackGrid props
  const memoizedColumns = useMemo(
    () => {
      let processedColumns = columns?.map(col => {
        const filterDef = Filters.find(f => f.name === col.name);
        const filterType = filterDef?.type || 'text';

        let dropdownOptions = null;
        if (filterType === 'select') {
          if (col.name === 'associationType') {
            const isGDA = tab === 'GDA';
            const options = isGDA ? dropdownLists.associationTypeGda : dropdownLists.associationTypeVda;
            dropdownOptions = options?.map(c => ({
              id: c,
              label: c.replace(/([A-Z])/g, ' $1').trim(),
            }));
          }
          else if (col.name === 'source') {
            const sourceList = dropdownLists.source[tab?.toLowerCase()] || dropdownLists.source.gda;
            dropdownOptions = sourceList?.map(s => ({ 
              id: s, 
              label: s === 'TEXTMINING_MODELS' ? 'MODELS' : s 
            }));
          }
          else if (dropdownLists[col.name]) {
            const options = dropdownLists[col.name];
            dropdownOptions = Array.isArray(options)
              ? options.map(opt => ({ id: opt, label: opt }))
              : options;
          }
        }

        return {
          ...col,
          filterType,
          dropdownOptions,
        };
      }) || [];



      return processedColumns;
    },
    [columns, tab],
  );

  const memoizedPageSizes = useMemo(() => getPageSizes(), [isAuthenticated, isTrial, restrictBrowseAll]);

  const memoizedSorting = useMemo(
    () => (orderByParam ? [{ id: orderByParam, desc: order === -1 }] : []),
    [orderByParam, order],
  );

  const memoizedFilterValues = useMemo(
    () =>
      defaultFilterValue.reduce((acc, filter) => {
        if (filter.value) {
          acc[filter.name] = filter.value;
        }
        return acc;
      }, {}),
    [defaultFilterValue],
  );

  const memoizedFilterOperators = useMemo(
    () =>
      defaultFilterValue.reduce((acc, filter) => {
        if (filter.operator) {
          acc[filter.name] = filter.operator;
        }
        return acc;
      }, {}),
    [defaultFilterValue],
  );

  const handlePageChange = useCallback(newPageIndex => setSkip(newPageIndex * limit), [limit]);

  const handlePageSizeChange = useCallback(newPageSize => {
    setLimit(newPageSize);
    setSkip(0);
  }, []);

  const handleSortingChange = useCallback(
    updater => {
      const newSorting =
        typeof updater === 'function'
          ? updater(orderByParam ? [{ id: orderByParam, desc: order === -1 }] : [])
          : updater;

      if (newSorting.length > 0) {
        setOrderByParam(newSorting[0].id);
        setOrder(newSorting[0].desc ? -1 : 1);
      } else {
        setOrderByParam(undefined);
        setOrder(undefined);
      }
    },
    [orderByParam, order],
  );

  const handleFilterChange = useCallback(
    (columnId, value) => {
      const newFilters = defaultFilterValue.map(f => (f.name === columnId ? { ...f, value } : f));
      onFilterValueChange(newFilters);
    },
    [defaultFilterValue, onFilterValueChange],
  );

  const handleFilterOperatorChange = useCallback(
    (columnId, operator) => {
      const newFilters = defaultFilterValue.map(f => (f.name === columnId ? { ...f, operator } : f));
      setDefaultFilterValue(newFilters);
      onFilterValueChange(newFilters);
    },
    [defaultFilterValue, onFilterValueChange],
  );

  // restriction handling
  if (
    (restrictGridDisplay && view === 'ALL') ||
    (restrictChemical && tab === 'CHEMICALS') ||
    (restrictChemical && view === 'CHEMICALS') ||
    (restrictChemical && isMapping && mappingContent?.tabSegment.includes('chem')) ||
    (restrictChemical && isMapping && mappingContent?.name === 'numberPmidsWithChemsIncludedInEvidence')
  ) {
    return (
      <ErrorMessage
        title="This content is not available with your current plan"
        subtitle={
          <div className="text-center">
            <button className="btn-gradient-pink" onClick={() => navigate('/plans')}>
              View our plans
            </button>
          </div>
        }
      />
    );
  }

  // no columns selected handling
  if (!columns || columns.length === 0) {
    return (
      <ErrorMessage
        title={'No columns selected'}
        subtitle={<span>Please select columns to display in the grid.</span>}
        style={{ minHeight: 537 }}
      />
    );
  }

  // Error handling
  if (isSessionExpired) {
    return <ErrorMessage title={'Session expired'} subtitle={<p>Please refresh the page</p>} />;
  }

  return (
    <>
      {isInvalidSource && (
        <ErrorMessage
          title={`No data available for "${source}" in ${tab}`}
          subtitle="Please remove or change the source filter."
        />
      )}

      {showCopiedAlert && <Alert message={alertMessage} type="success" />}

      <ActiveFiltersBar
        filters={defaultFilterValue}
        onRemoveFilter={handleRemoveFilter}
        onClearAll={handleClearAllFilters}
        isEvidence={isEvidence}
      />

      <TanStackGrid
        key={`${tab}-${view}-${source}-${isEvidence ? 'evidence' : 'summary'}`}
        columns={memoizedColumns}
        data={rowData}
        loading={loading}
        pagination={true}
        manualPagination={true}
        totalCount={totalCount}
        pageIndex={Math.floor(skip / limit)}
        pageSize={limit}
        pageSizeOptions={memoizedPageSizes}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        manualSorting={true}
        sorting={memoizedSorting}
        onSortingChange={handleSortingChange}
        enableRowSelection={isEvidence && SHOW_SUMMARIZE}
        onRowSelectionChange={onSelectionChange}
        showFilters={true}
        filterValues={memoizedFilterValues}
        filterOperators={memoizedFilterOperators}
        onFilterChange={handleFilterChange}
        onFilterOperatorChange={handleFilterOperatorChange}
        columnOrder={columnOrder}
        onColumnOrderChange={setColumnOrder}
        columnVisibilityModel={columnVisibilityModel}
      />
      {showPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            <button className="w-full text-right pb-2 pr-2" onClick={handleClosePopup}>
              X
            </button>
            {!isAuthenticated && (
              <p className="text-center text-base">
                As an unregistered user, you are seeing only the top 10 results (sorted by score) for each
                query. Register to view more. Learn about our{' '}
                <a href="/plans" style={{ color: '#D02591' }}>
                  plans here
                </a>
                .
              </p>
            )}
            {restrictBrowseAll && (
              <p className="text-center text-base">
                You are viewing the top 30 results, ordered by score for each query. To view more,{' '}
                <a href="/Profile-area" style={{ color: '#D02591' }}>
                  upgrade your account.
                </a>{' '}
                <br /> Learn about our{' '}
                <a href="/plans" style={{ color: '#D02591' }}>
                  plans here
                </a>
                .
              </p>
            )}
            {isTrial && (
              <p className="text-center text-base">
                Only the top 30 results are shown.{' '}
                <a href="/Profile-area" style={{ color: '#D02591' }}>
                  Upgrade your account{' '}
                </a>
                to see all results.
                <br /> Learn about our{' '}
                <a href="/plans" style={{ color: '#D02591' }}>
                  plans here
                </a>
                .
              </p>
            )}
          </div>
        </div>
      )}

      <SharedDialog
        isOpen={showFiltersModal}
        onClose={() => setShowFiltersModal(false)}
        title="Do you want to save this search?"
        confirmText="Yes"
        cancelText="No"
        hasConfirmBtn
        onConfirm={handleSaveFilters}
      >
        <div className="grid">
          <span>
            <strong>Tab: </strong>
            {tab}
          </span>
          <span>
            <strong>View: </strong>
            {view}
          </span>
          <span>
            <strong>Source: </strong>
            {source}
          </span>
          {!isEmpty(searchItems) && (
            <span>
              <strong>Idents: </strong>
              {searchItems.map(item => item.id).join(', ')}
            </span>
          )}
          <div>
            {pendingFiltersToSave && pendingFiltersToSave.length > 0 && (
              <>
                <strong>Filters: </strong>
                {pendingFiltersToSave.map(filter => getFilterLabel(filter)).join(', ')}
              </>
            )}
          </div>
        </div>
      </SharedDialog>
    </>
  );
}

export default Grid;
